'use client'

import { Cell, Text } from '@vinted/web-ui'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'

import { SupportingDocumentType } from '../../../../constants'
import EducationModal from './EducationModal/EducationModal'
import FileInput from '../../../FileInput'
import { toBase64 } from '../../../../utils'

type Props = {
  fieldName: string
  identifier: string
  title: string
  type: string
  isDisabled: boolean
  mediaTypes?: Array<string>
  maximumFileSize: number
  apiErrors?: Array<ErrorItem>
  index?: number
}

const BankStatementDocumentFile = ({
  fieldName,
  identifier,
  title,
  type,
  isDisabled,
  mediaTypes,
  apiErrors,
  maximumFileSize,
  index,
}: Props) => {
  const translate = useTranslate()

  const {
    control,
    register,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext()

  const validationMessage = useFormValidationMessage(errors)

  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false)

  const fileInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    register(`${fieldName}.identifier`, { required: true })
    register(`${fieldName}.body`, { required: true })
    register(`${fieldName}.mediaType`, { required: true })

    setValue(`${fieldName}.identifier`, identifier)
  }, [register, setValue, fieldName, identifier])

  useEffect(() => {
    const bankStatementError = apiErrors?.find(
      apiError => apiError.field === SupportingDocumentType.BankStatement,
    )

    if (!bankStatementError) return

    setError(type, { type: 'manual', message: bankStatementError.value })
  }, [apiErrors, type, setError])

  const handleChange = async (file: File | null, error?: string) => {
    setValue(`${fieldName}.file`, file)

    if (file) {
      const encodedFile = await toBase64({ file, removeMetadata: true })
      const mediaType = file.type

      setValue(`${fieldName}.body`, encodedFile)
      setValue(`${fieldName}.mediaType`, mediaType)
    }

    if (error) {
      setError(type, { type: 'manual', message: error })
    } else {
      clearErrors(type)
    }

    clearErrors(`${fieldName}.file`)
  }

  const openInfoModal = () => {
    setIsInfoModalVisible(true)
  }

  const closeInfoModal = () => {
    setIsInfoModalVisible(false)
  }

  const openUploadDialog = () => {
    setIsInfoModalVisible(false)

    fileInput.current?.click()
  }

  const getValidationError = (field: string) => validationMessage(field) || validationMessage(type)

  const renderInfoModal = () => {
    return (
      <EducationModal
        show={isInfoModalVisible}
        onClose={closeInfoModal}
        onUpload={openUploadDialog}
      />
    )
  }

  const handleFileInputButtonClick = (currentButtonState: 'upload' | 'delete') => {
    if (currentButtonState === 'upload') {
      openInfoModal()

      return
    }

    setValue(`${fieldName}.file`, null)
  }

  const inputInformationLink = {
    information_link: (chunks: Array<ReactNode>) => (
      <button type="button" onClick={openInfoModal}>
        <Text as="span" clickable text={chunks} type={Text.Type.Subtitle} />
      </button>
    ),
  }

  return (
    <>
      <Controller
        control={control}
        rules={{
          required: translate('kyc_form.supporting_documents.bank_statement.errors.required'),
        }}
        name={`${fieldName}.file`}
        defaultValue={null}
        render={({ field: { value, name } }) => (
          <FileInput
            index={index}
            disabled={isDisabled}
            acceptedFileTypes={mediaTypes?.join(',')}
            styling={Cell.Styling.Wide}
            value={value}
            title={title}
            name={name}
            maximumFileSize={maximumFileSize}
            fileInputRef={fileInput}
            isInfoLinkVisible
            onChange={handleChange}
            onButtonClick={handleFileInputButtonClick}
            translationPrefix="kyc_form.supporting_documents.upload"
            validation={renderValidation(getValidationError(`${fieldName}.file`))}
            inputInformationLink={inputInformationLink}
          />
        )}
      />
      {renderInfoModal()}
    </>
  )
}

export default BankStatementDocumentFile
